import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import { utility } from './utility';
import { url$ } from '../enums';
import { i18n } from '@/locales';
export interface DownloadOptions extends AxiosRequestConfig {
    forceFileName?: string;
    autoDownload?: boolean; // 預設應為 true
}

export class http {
    private instance: AxiosInstance;
    private parent: utility;

    constructor(parent: utility) {
        this.parent = parent;
        this.instance = axios.create({
            baseURL: import.meta.env.VITE_API_BASE_URL
        });

        this.setupInterceptors();
    }

	/**
 * 🎯 公開方法：讓外部（如 SignalR）也能呼叫統一的錯誤處理
 * @param error - 錯誤物件 (axios 或 SignalR 格式)
 * @param options - 額外選項
 */
	public handleError(error: any, options?: {
		silent?: boolean;        // 是否不顯示 toast/messenger
		skipRedirect?: boolean;  // 401 時是否跳過導向登入
	}): void {
		// 標記為外部呼叫，避免重複 reject
		if (options?.silent && error.config) {
			error.config.silent = true;
		}

		// 如果是 axios 錯誤，直接交給原有邏輯
		if (error.isAxiosError !== undefined) {
			this.handleAxiosError(error);
			return;
		}

		// 🔄 SignalR / 一般錯誤格式轉換為 axios 相容格式
		const normalizedError = this.normalizeSignalRError(error);
		this.handleAxiosError(normalizedError);
	}

	/**
	 * 🔧 將 SignalR 錯誤轉換為 axios 相容格式
	 */
	private normalizeSignalRError(error: any): any {
		// 預設結構
		const normalized: any = {
			isAxiosError: true, // 騙過 handleAxiosError 的 axios.isCancel 檢查
			message: error?.message || error?.toString() || 'Unknown SignalR error',
			response: null
		};

		// 嘗試萃取 HTTP 狀態碼（SignalR 錯誤訊息通常包含 "401"、"Unauthorized"）
		const msg = (error?.message || '').toLowerCase();
		let statusCode: number | null = null;

		if (msg.includes('401') || msg.includes('unauthorized')) statusCode = 401;
		else if (msg.includes('403') || msg.includes('forbidden')) statusCode = 403;
		else if (msg.includes('404') || msg.includes('not found')) statusCode = 404;
		else if (msg.includes('500') || msg.includes('internal')) statusCode = 500;

		if (statusCode) {
			normalized.response = {
				status: statusCode,
				data: {
					title: this.getHttpStatusText(statusCode),
					detail: error?.message || 'Connection failed',
					traceId: 'N/A',
					code: statusCode === 401 ? 'UNAUTHORIZED' : undefined
				}
			};
		} else {
			// 非 HTTP 錯誤（如網路中斷）
			normalized.response = {
				status: 0,
				data: {
					title: 'Connection Error',
					detail: error?.message || 'Unable to connect to server',
					traceId: 'N/A'
				}
			};
		}

		return normalized;
	}

	/**
	 * 📦 提供 SignalR 共用的 Auth 設定（避免重複讀取 token 邏輯）
	 */
	public getAuthConfig(): { accessTokenFactory: () => string } {
		return {
			accessTokenFactory: () => {
				return this.parent.pinia$.security.token() || '';
			}
		};
	}

	/**
	 * 🔤 輔助：HTTP 狀態碼轉文字
	 */
	private getHttpStatusText(status: number): string {
		const map: Record<number, string> = {
			400: 'Bad Request',
			401: 'Unauthorized',
			403: 'Forbidden',
			404: 'Not Found',
			500: 'Internal Server Error',
			503: 'Service Unavailable',
			504: 'Gateway Timeout'
		};
		return map[status] || 'Server Error';
    }

    /** HTTP 遮蔽：前端硬底線黑名單（不分大小寫；不可被後端覆蓋、只能加碼）。★新增敏感欄位務必加入此清單。 */
    private static readonly MASK_KEYS_BUILTIN = [
        'secret', 'nSecret', 'password', 'passwordHash',
        'token', 'accessToken', 'key', 'code', 'authorization'
    ];
    private static readonly MASK = '***';
    private static readonly MASK_MAXLEN = 200;

    /**
     * 取當前生效的遮蔽 key 聯集（前端硬底線 ∪ 後端 ClientLog.MaskKeys）。
     * 每次呼叫即時讀 parameters，故 Bootstrap 後自動含後端清單；登入前只有硬底線。
     */
    private maskKeySet(): Set<string> {
        const fromServer = (this.parent.pinia$.app.parameters?.clientLog?.maskKeys as string[] | undefined) ?? [];
        return new Set([...http.MASK_KEYS_BUILTIN, ...fromServer].map(k => k.toLowerCase()));
    }

    /**
     * 遞迴遮蔽 JSON 欄位值（命中黑名單→***，長字串截斷）。
     * walk 只讀不寫、回傳新結構，不動原 body（送出的真實 request 不受影響）。
     */
    private maskJson(input: any): any {
        const set = this.maskKeySet();
        const walk = (v: any): any => {
            if (Array.isArray(v)) return v.map(walk);
            if (v && typeof v === 'object') {
                const out: any = {};
                for (const k of Object.keys(v)) {
                    out[k] = set.has(k.toLowerCase()) ? http.MASK : walk(v[k]);
                }
                return out;
            }
            if (typeof v === 'string' && v.length > http.MASK_MAXLEN) {
                return v.slice(0, http.MASK_MAXLEN) + '…(truncated)';
            }
            return v;
        };
        return walk(input);
    }

    /**
     * request/response body 的 log 片段：
     * JSON 物件 → 遮蔽後印；非 JSON（FormData/Blob/字串）→ 略過標記；空 → 不印 body。
     */
    private bodyForLog(body: any): any[] {
        if (body === undefined || body === null) return [];
        if (typeof body === 'object' && !(body instanceof FormData) && !(body instanceof Blob)) {
            return ['body:', this.maskJson(body)];
        }
        return ['[non-JSON body skipped]'];
    }

    private setupInterceptors() {
        // Request Interceptor: 注入 JWT Token
        this.instance.interceptors.request.use(config => {
            const token = this.parent.pinia$.security.token();
            if (token) {
                // 建議寫法：確保 Authorization 存在於 headers 中
                if (config.headers) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
            }
            // HTTP 摘要 log（只在 debug 才做，避免平常損耗）。耗時：此處記起點、response 相減。
            if (this.parent.log$.isDebug()) {
                (config as any).__t0 = performance.now();
                const parts: any[] = [`[HTTP →] ${(config.method || 'GET').toUpperCase()} ${config.url ?? ''}`];
                if (config.params && typeof config.params === 'object') {
                    parts.push('params:', this.maskJson(config.params));
                }
                parts.push(...this.bodyForLog(config.data));
                this.parent.log$.debug(...parts);
            }
            return config;
        }, error => {
            // 記錄詳細 Log 供開發者排查
            this.parent.log$.error('[Request Interceptor Error]', error);
            // 彈出警告，告訴使用者「瀏覽器或請求格式有問題」
            this.parent.toast$?.error(i18n.global.t('http.requestFailedSummary'),
                i18n.global.t('http.requestFailedDetail'),
                'r', 5000);
            return Promise.reject(error);
        });

        // Response Interceptor
        this.instance.interceptors.response.use(
            (response: AxiosResponse) => {
                // 如果是檔案流，回傳整個 response (包含 header 中的 filename)；blob 不印 log。
                if (response.config.responseType === 'blob') {
                    return response;
                }
                // HTTP 摘要 log（只在 debug 才做）。response.data 已是解析後 JSON → 用副本遮蔽印，回傳仍原值。
                if (this.parent.log$.isDebug()) {
                    const t0 = (response.config as any).__t0;
                    const ms = (t0 != null) ? Math.round(performance.now() - t0) : undefined;
                    const head = `[HTTP ←] ${(response.config.method || 'GET').toUpperCase()} ${response.config.url ?? ''} ${response.status}${ms != null ? ` (${ms}ms)` : ''}`;
                    this.parent.log$.debug(head, ...this.bodyForLog(response.data));
                }
                return response.data;
            },
            (error) => {
                // 這裡調用 utility 統一的錯誤處理
                try {
                    this.handleAxiosError(error);
                }
                finally {
                    return Promise.reject(error);
                }
            }
        );
    }

    private handleAxiosError(error: any) {
        if (axios.isCancel(error)) {
            this.parent.log$.debug('Request canceled:', error.message);
            return; // 直接結束，不跳 Toast/Messenger
        }
        const response = error.response;
        const status = response?.status;

        // 優先處理 401 過期
        if (status === 401) {
            const code = response.data?.code;
            // 如果後端有傳特定的 sub-code，代表只是密碼錯，不要登出
            if (code === 'INVALID_SECRET') {
                //更改私密再次輸入密碼錯誤
                return;
            } else if (code == 'ACCOUNT_LOCKED') {
                //輸入太多次被鎖定
                return;
            }
            this.parent.pinia$.security.sessionExpired(
                `<strong>${i18n.global.t('common.sessionExpired.tokenExpired.summary')}</strong><br/>${i18n.global.t('common.sessionExpired.tokenExpired.detail')}`
            );
            return;
        } else if (status === 403) {
            // 權限不足：已登入但無權限。非系統錯誤 → warn 級別 + 較溫和提示，不跳登入。
            if (!error.config?.silent) {
                const data = response.data;
                const title = (data && typeof data === 'object') ? data.title : i18n.global.t('http.forbiddenSummary');
                const detail = (data && typeof data === 'object') ? data.detail : i18n.global.t('http.forbiddenDetail');
                this.parent.toast$?.warn(title, detail, 'c', -1);   // warn 而非 error
                this.parent.log$.warn(
                    `Forbidden | [Http 403] | TraceId: ${(data && typeof data === 'object') ? data.traceId : 'N/A'} | Instance: ${(data && typeof data === 'object') ? data.instance : ''}`);
            }
            return;
        } else if (status == 400) {
            if (response.data?.code == 'INVALID_CAPTCHA')
                return;
        }

        // 提取後端傳回的標準 JSON (ProblemDetails)
        // 提取後端傳回的標準 JSON，但先判斷 data 是否為物件
        const isObjectError = response?.data && typeof response.data === 'object';
        const errorData = isObjectError ? response.data : {
            title: status === 504 ? 'Gateway Timeout' : 'Server Error',
            detail: typeof response?.data === 'string' ? 'The server returned an unexpected response.' : error.message,
            traceId: 'N/A'
        };

        if (errorData.traceId != 'N/A') { 
            if (!error.config?.silent) {
                this.parent.toast$?.error(errorData.title,
                    `${errorData.detail}\r\n\r\nTrace ID: ${errorData.traceId}`, 'c', -1);
                this.parent.log$.error(
                    `System Request Error | [Http ${response?.status}] | TraceId: ${errorData.traceId}| Instance: ${errorData.instance}`);
            }
        } else {
            if (!error.config?.silent) {
                this.parent.log$.error(response?.data || 'Unknown error');
            }
        }
    }

    /**
     * 從 catch 到的錯誤統一萃取後端回應內容（ProblemDetails / 自訂 code）。
     *
     * 為何需要此方法：回應攔截器「成功 return response.data（已拆）、失敗 reject 原始 error（未拆）」。
     * 成功路徑直接用 response.xxx；失敗路徑請一律經此方法取值，不要在呼叫端直接寫 error.response.data.xxx，
     * 以免漏 .data（取到 undefined）或誤加 .data。
     *
     * 相容 axios error 與經 normalizeSignalRError 正規化後的錯誤（兩者 body 皆在 error.response.data）。
     *
     * @returns 一律提供 code/title/detail/status/traceId 欄位（取不到則為 undefined），呼叫端直接解構使用。
     */
    public extractError(error: any): {
        code?: string;
        title?: string;
        detail?: string;
        traceId?: string;
        status?: number;
        raw: any;
    } {
        const data = error?.response?.data;
        const body = (data && typeof data === 'object') ? data : undefined;
        return {
            code: body?.code,
            title: body?.title,
            detail: body?.detail,
            traceId: body?.traceId,
            status: error?.response?.status,
            raw: error,
        };
    }

    public get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.instance.get(url, config);
    }

    public post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.instance.post(url, data, config);
    }

    public put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.instance.put(url, data, config);
    }

    public delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.instance.delete(url, config);
    }

    public patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.instance.patch(url, data, config);
    }

    /**
     * 私有工具：將 Blob 轉為瀏覽器下載動作
     */
    private downloadBlob(response: AxiosResponse<Blob>, options?: DownloadOptions): Blob {
        const blob = response.data;
        let disposition = response.headers['content-disposition'];

        if (Array.isArray(disposition)) {
            disposition = disposition[0];
        }

        let fileName = options?.forceFileName || 'download';
        let isParsed = false; // 🚩 新增：解析旗標

        if (!options?.forceFileName && typeof disposition === 'string') {
            // 1. 優先處理 filename* (RFC 5987)
            const filenameStarMatch = disposition.match(/filename\*=([^;]+)/);
            if (filenameStarMatch && filenameStarMatch[1]) {
                const segments = filenameStarMatch[1].split("'");
                const encodedValue = segments[segments.length - 1];
                if (encodedValue) {
                    try {
                        fileName = decodeURIComponent(encodedValue);
                        isParsed = true; // ✅ 標記為解析成功
                    } catch (e) { }
                }
            }

            // 2. 備用方案：只有在「沒有強制檔名」且「filename* 沒解析成功」時才執行
            if (!isParsed) {
                const filenameMatch = disposition.match(/filename=(["']?)(.*?)\1(?:;|$)/);
                if (filenameMatch && filenameMatch[2]) {
                    try {
                        fileName = decodeURIComponent(filenameMatch[2]);
                    } catch (e) {
                        fileName = filenameMatch[2]; // 解碼失敗則用原文
                    }
                }
            }
        }

        // 3. 安全清理與執行下載 (其餘邏輯不變)
        fileName = fileName.replace(/[\\/:*?"<>|]/g, '_');

        if (options?.autoDownload !== false) {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(url), 200);
        }

        return blob;
    }

    /** GET 下載 */
    public async getFile(url: string, options?: DownloadOptions): Promise<Blob> {
        const response = await this.instance.get<Blob>(url, {
            ...options,
            responseType: 'blob'
        });
        return await this.downloadBlob(response, options);
    }

    /** POST 下載 */
    public async downloadFile(url: string, data?: any, options?: DownloadOptions): Promise<Blob> {
        const response = await this.instance.post<Blob>(url, data, {
            ...options,
            responseType: 'blob'
        });
        return await this.downloadBlob(response, options);
    }

    /**
    * 上傳檔案 (支援多檔案與額外 JSON 資料)
    * @param url API 路徑
    * @param data 已經組裝好的 FormData
    */
    public uploadFiles<T>(url: string, data: FormData, config?: AxiosRequestConfig): Promise<T> {
        return this.instance.post(url, data, {
            // 先展開傳入的 config
            ...config,
            // 確保 headers 被正確合併
            headers: {
                "Content-Type": "multipart/form-data",
                ...config?.headers // 如果外部有傳自定義 headers，也一併合併進來
            }
        });
    }
}